import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Check, ChevronDown, Plus } from "lucide-react"
import { cn } from "@/utils"
import { ComponentChildren } from "preact"
import { useState } from "preact/hooks"

export interface SearchableSelectOption {
  value: string
  label: string
  /** Heading to list the option under. Options sharing one are shown together, in first-seen order. */
  group?: string
  /** A short green tag after the label, for information that is not a restriction ("In this forecast"). */
  badge?: string
}

/** PURE: options split into their `group` headings, in the order each heading first appears. */
export function group_options(options: SearchableSelectOption[]): Array<{ heading?: string; options: SearchableSelectOption[] }> {
  const groups: Array<{ heading?: string; options: SearchableSelectOption[] }> = []
  for (const option of options) {
    const existing = groups.find((group) => group.heading === option.group)
    if (existing) existing.options.push(option)
    else groups.push({ heading: option.group, options: [option] })
  }
  return groups
}

interface SearchableSelectProps {
  options: SearchableSelectOption[]
  value: string
  onValueChange: (value: string) => void
  onSearchChange?: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  triggerClassName?: string
  /** Extra classes for the floating popover content (e.g. to lock width to the trigger). */
  contentClassName?: string
  /** Extra classes for the scrollable options list (e.g. to raise the max height). */
  listClassName?: string
  renderTrigger?: (selectedOption?: SearchableSelectOption, isOpen?: boolean) => ComponentChildren
  /** When true, use a modal popover layer (e.g. inside Dialog) for focus and scroll behavior. */
  insideModal?: boolean
  /** Observe the popover opening/closing (e.g. to commit or clear a pending search on dismiss). */
  onOpenChange?: (open: boolean) => void
  /**
   * Offer the typed search text as a new option ("Add …") when it is not already one. Called with
   * the trimmed text; the caller stores it and must include it in `options` to have it displayed.
   */
  onCreateOption?: (text: string) => void
  /** Wording of the add row; defaults to `Add "<text>"`. */
  createOptionLabel?: (text: string) => string
}

/**
 * The text to offer as a new option for a search: the trimmed search, or null when it is empty or
 * already names an option (ignoring case and surrounding spaces, so "kehe " does not offer a second
 * "KeHE").
 */
export function creatable_option_text(search: string, options: SearchableSelectOption[]): string | null {
  const text = search.trim()
  if (!text) return null
  const normalized = text.toLowerCase()
  const exists = options.some(
    (option: SearchableSelectOption) =>
      option.label.trim().toLowerCase() === normalized || option.value.trim().toLowerCase() === normalized
  )
  return exists ? null : text
}

export function SearchableSelect({
  options,
  value,
  onValueChange,
  onSearchChange,
  placeholder,
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  className,
  triggerClassName,
  contentClassName,
  listClassName,
  renderTrigger,
  insideModal = false,
  onOpenChange,
  onCreateOption,
  createOptionLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const selectedOption = options.find(opt => opt.value === value)
  const displayText = selectedOption?.label ?? placeholder ?? "Select..."
  const creatable_text = onCreateOption ? creatable_option_text(search, options) : null

  function set_open(next: boolean) {
    setOpen(next)
    if (!next) setSearch("")
    onOpenChange?.(next)
  }

  function handleSelect(optionValue: string) {
    onValueChange(optionValue)
    set_open(false)
  }

  function handle_search_change(next: string) {
    setSearch(next)
    onSearchChange?.(next)
  }

  function handle_create(text: string) {
    onCreateOption?.(text)
    set_open(false)
  }

  function defaultTrigger() {
    return (
      <Button
        variant="outline"
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-plue-100 placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-plue-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          triggerClassName
        )}>
        <span className="line-clamp-1">{displayText}</span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </Button>
    )
  }

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={set_open} modal={insideModal}>
        <PopoverTrigger asChild>
          {renderTrigger ? renderTrigger(selectedOption, open) : defaultTrigger()}
        </PopoverTrigger>
        <PopoverContent
          className={cn("min-w-[var(--radix-popover-trigger-width)] p-0", contentClassName)}
          align="start"
          onWheel={(e) => {
            // Allow scrolling within the popover (on sales forecasting dialog)
            e.stopPropagation()
          }}
          onTouchMove={(e) => {
            // Allow touch scrolling within the popover
            e.stopPropagation()
          }}
        >
          <Command>
            <CommandInput placeholder={searchPlaceholder} onValueChange={handle_search_change} />
            <CommandList className={listClassName}>
              {/* With an add row to fall back on, "nothing found" is not the end of the road. */}
              {!creatable_text && <CommandEmpty>{emptyMessage}</CommandEmpty>}
              {group_options(options).map((group) => (
                <CommandGroup key={group.heading ?? ""} heading={group.heading}>
                  {group.options.map((item) => (
                    <CommandItem
                      key={item.value}
                      value={item.label}
                      onSelect={() => handleSelect(item.value)}>
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === item.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span>{item.label}</span>
                      {item.badge ? (
                        <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800">
                          {item.badge}
                        </span>
                      ) : null}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
              {creatable_text && (
                // forceMount: cmdk hides rows the search text does not match, and this row has to
                // show for text that matches nothing.
                <CommandGroup forceMount>
                  <CommandItem
                    forceMount
                    value={`__create__ ${creatable_text}`}
                    onSelect={() => handle_create(creatable_text)}>
                    <Plus className="mr-2 h-4 w-4" />
                    <span>{createOptionLabel ? createOptionLabel(creatable_text) : `Add "${creatable_text}"`}</span>
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("group_options", () => {
    it("keeps ungrouped options in one group and groups the rest in first-seen order", () => {
      expect(group_options([{ value: "a", label: "A" }, { value: "b", label: "B" }])).toEqual([
        { heading: undefined, options: [{ value: "a", label: "A" }, { value: "b", label: "B" }] },
      ])
      const grouped = group_options([
        { value: "TPR", label: "TPR", group: "Price promotion" },
        { value: "Scan", label: "Scan", group: "Scan or chargeback" },
        { value: "OI", label: "OI", group: "Price promotion" },
      ])
      expect(grouped.map((group) => [group.heading, group.options.map((option) => option.value)])).toEqual([
        ["Price promotion", ["TPR", "OI"]],
        ["Scan or chargeback", ["Scan"]],
      ])
    })
  })

  describe("creatable_option_text", () => {
    const options = [
      { value: "KeHE", label: "KeHE" },
      { value: "unfi-east", label: "UNFI - East" },
    ]

    it("offers trimmed text that names no option", () => {
      expect(creatable_option_text("  Dot Foods ", options)).toBe("Dot Foods")
    })

    it("offers a partial match, which is still a different name", () => {
      expect(creatable_option_text("UNFI", options)).toBe("UNFI")
    })

    it("does not offer an existing option under another casing", () => {
      expect(creatable_option_text("kehe ", options)).toBeNull()
      expect(creatable_option_text("unfi - east", options)).toBeNull()
    })

    it("offers nothing for an empty search", () => {
      expect(creatable_option_text("   ", options)).toBeNull()
    })
  })
}
